import JSONModel from "sap/ui/model/json/JSONModel";
import Event from "sap/ui/base/Event";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import BaseController from "de/realcore/realseats/controller/BaseController";
import FilterBar from "sap/ui/comp/filterbar/FilterBar";
import Table from "sap/m/Table";
import type FilterItem from "sap/ui/comp/filterbar/FilterItem";
import ComboBox from "sap/m/ComboBox";
import DatePicker from "sap/m/DatePicker";
import SearchField from "sap/m/SearchField";
import SelectDialog from "sap/m/SelectDialog";
import Dialog from "sap/m/Dialog";
import Fragment from "sap/ui/core/Fragment";
import Control from "sap/ui/core/Control";
import Button from "sap/m/Button";
import Input from "sap/m/Input";
import List from "sap/m/List";
import StandardListItem from "sap/m/StandardListItem";

import MessageToast from "sap/m/MessageToast";
import MessageBox from "sap/m/MessageBox";
import CheckBox from "sap/m/CheckBox";
import { Route$MatchedEvent } from "sap/ui/core/routing/Route";
import ApiService from "../src/api/ApiService";
import formatter from "../src/utils/formatter";
import { Room, Seat } from "../src/types";
import { HomeZoneAuthUtil } from "../src/utils/HomeZoneAuthUtil";

import { TIME_RANGES, BUSINESS_HOURS, DEFAULT_EVENT_ID } from "../src/constants/TimeConstants";
import {
    isTimeGreaterOrEqual,
    getCurrentTimePlusMinutes,
    calculateEndTimeFromStart,
    validateAndAdjustStartTime,
    shouldUseFlexibleTime,
    getMaxBookingDate
} from "../src/utils/TimeUtils";
import { filterTimeRangesByBookings, isSeatFullyBookedForDay } from "../src/utils/BookingUtils";

/**
 * @namespace de.realcore.realseats.controller
 */
export default class FreeSpacesSeats extends BaseController {
    public formatter = formatter;
    private seatsModel!: JSONModel;
    private roomModel!: JSONModel;
    private _dialog: Dialog | null = null;
    private _employeeDialog: Dialog | null = null;
    private selectedEmployeeFromDialog: string | null = null;
    private selectedEmployeeId: string | null = null;
    private cancelDialog!: Dialog;
    private _oFilterBar!: FilterBar;
    private _oTable!: Table;
    private authToken: string;
    private selectedDate: string;
    private roomId: string;
    private apiService: ApiService;
    private readonly processingFavorites: Set<string> = new Set(); // Track ongoing favorite operations
    private isUpdatingFromHashChange: boolean = false; // Flag to prevent URL updates during hash change handling

    public onInit(): void {
        const oView = this.getView();

        // Initialize API service
        this.apiService = new ApiService(this.getOwnerComponent());

        // Initialize HomeZoneAuthUtil with API service
        HomeZoneAuthUtil.initialize(this.apiService);

        // Initialize models
        this.seatsModel = new JSONModel({ seats: [], selectedDate: "", loading: false });
        this.roomModel = new JSONModel({ selectedRoom: {} });

        oView.setModel(this.seatsModel, "SeatsModel");
        oView.setModel(this.roomModel, "RoomModel");

        // Load employee data for the SeatBookingDialog fragment
        this.loadEmployeeData();

        // Set up date picker with current date (will be overridden by _onMatched if date parameter is provided)
        const datePicker = oView.byId("idSeatsDatePickerDatePicker") as DatePicker;
        if (datePicker) {
            const today = new Date();
            datePicker.setValue(today.toISOString().split("T")[0]);
            this.selectedDate = formatter.formatLocalDateTime(today);
            this.seatsModel.setProperty(
                "/selectedDate",
                formatter.formatDate(formatter.formatLocalDateTime(today))
            );
        }

        this._oFilterBar = oView.byId("idSeatsFilterBar") as FilterBar;
        this._oTable = oView.byId("idSeatsTable") as Table;

        // Set up routing
        this.getOwnerComponent()
            .getRouter()
            .getRoute("FreeSpacesSeats")
            .attachPatternMatched(this._onMatched.bind(this));

        // Listen for route changes to handle browser back navigation
        this.getOwnerComponent().getRouter().attachRouteMatched(this._onAnyRouteMatched.bind(this));

        // Add hashchange listener for browser navigation
        window.addEventListener("hashchange", this._onHashChange.bind(this));
    }

    private _onAnyRouteMatched(oEvent: any): void {
        const sRouteName = oEvent.getParameter("name");

        // If navigating away from FreeSpacesSeats to FreeSpaces (e.g., browser back button)
        if (sRouteName === "FreeSpaces" && this.getView()?.getId()) {
            console.log("Detected navigation from FreeSpacesSeats to FreeSpaces via browser");

            // Get current date from the date picker
            const datePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
            const selectedDate = datePicker?.getDateValue();

            if (selectedDate) {
                // Format date using local timezone
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const dateString = `${year}-${month}-${day}`;

                console.log("Browser navigation detected, updating URL with date:", dateString);

                // Update the URL to include the date parameter
                // Use replace to avoid adding to browser history
                const newHash = `#freespaces?date=${dateString}`;
                window.history.replaceState(null, "", newHash);
            }
        }
    }

    private async _onMatched(oEvent: Route$MatchedEvent): Promise<void> {
        const sRoomId = (oEvent.getParameter("arguments") as any).room;
        this.roomId = sRoomId;

        // Extract date from query parameters if provided
        const routeArguments = oEvent.getParameter("arguments") as any;
        const queryParams = routeArguments["?query"] || {};
        const dateParam = queryParams.date;

        // Set the date picker value based on URL parameter or keep current value
        const datePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
        if (dateParam && datePicker) {
            try {
                // Parse the date parameter (expected format: YYYY-MM-DD) using local timezone
                const [year, month, day] = dateParam.split("-").map(Number);
                const parsedDate = new Date(year, month - 1, day); // month is 0-indexed
                if (!isNaN(parsedDate.getTime())) {
                    // Set flag to prevent URL updates during route parameter handling
                    this.isUpdatingFromHashChange = true;

                    datePicker.setDateValue(parsedDate);
                    this.selectedDate = formatter.formatLocalDateTime(parsedDate);
                    this.seatsModel.setProperty(
                        "/selectedDate",
                        formatter.formatDate(formatter.formatLocalDateTime(parsedDate))
                    );
                    console.log("Date parameter received and set:", dateParam, parsedDate);

                    // Reset flag after processing
                    this.isUpdatingFromHashChange = false;
                } else {
                    console.warn("Invalid date parameter received:", dateParam);
                }
            } catch (error) {
                console.error("Error parsing date parameter:", dateParam, error);
            }
        }

        // Clear existing seat data immediately to prevent showing old data
        this.seatsModel.setProperty("/seats", []);
        this.seatsModel.setProperty("/loading", true);

        try {
            const oComponent = this.getOwnerComponent();
            this.authToken = await oComponent.getAccessToken();

            // Fetch room details first
            this.fetchRoomDetails(sRoomId);

            // Then fetch seats for the room
            await this.fetchSeatsForRoom(sRoomId);
        } catch (error) {
            console.error("Error fetching data:", error);
            MessageToast.show(this.getText("errorLoadingRoomData"));
        } finally {
            // Clear loading state
            this.seatsModel.setProperty("/loading", false);
        }
    }

    private fetchRoomDetails(roomId: string): void {
        try {
            // This would typically call an API to get room details
            // For now, using mock data structure similar to existing implementation
            const roomDetails = {
                id: roomId,
                name: `Room ${roomId}`,
                floorPlan: { name: this.getText("groundFloor") },
                description: this.getText("meetingRoom")
            };

            this.roomModel.setProperty("/selectedRoom", roomDetails);
        } catch (error) {
            console.error("Error fetching room details:", error);
        }
    }

    private async fetchSeatsForRoom(roomId: string): Promise<void> {
        try {
            const oComponent = this.getOwnerComponent();
            this.authToken = await oComponent.getAccessToken();

            // Fetch room details
            const roomData = await this.apiService.room.fetchById(roomId);
            if (roomData) {
                this.roomModel.setProperty("/selectedRoom", roomData);
            }

            // Get the selected date for availability check
            const datePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
            const selectedDate = datePicker?.getDateValue() || new Date();

            const startDateTime = new Date(selectedDate);
            startDateTime.setHours(0, 0, 0, 0);

            const endDateTime = new Date(selectedDate);
            endDateTime.setHours(23, 59, 59, 999);

            // Get ALL seats in the room (not just available ones)
            const allSeatsInRoom = await this.apiService.room.fetchSeatsByRoom(roomId);

            // Get ALL bookings for the selected date (from all users)
            const allBookings = await this.apiService.booking.fetchAllBookings();

            // Get current user's bookings for the selected date to determine seat status
            const myBookings = await this.apiService.booking.fetchMyBookings();

            // Filter ALL bookings for the selected date
            const selectedDateString = formatter.formatDate(
                formatter.formatLocalDateTime(selectedDate)
            );
            const allTodaysBookings = allBookings.filter((booking: any) => {
                const bookingDate = new Date(booking.startTime);
                const bookingDateString = formatter.formatDate(
                    formatter.formatLocalDateTime(bookingDate)
                );
                return bookingDateString === selectedDateString && !booking.cancelled;
            });

            // Filter user's bookings for the selected date
            const userTodaysBookings = myBookings.filter((booking: any) => {
                const bookingDate = new Date(booking.startTime);
                const bookingDateString = formatter.formatDate(
                    formatter.formatLocalDateTime(bookingDate)
                );
                return bookingDateString === selectedDateString && !booking.cancelled;
            });

            // Apply department and home zone filtering first to get only authorized seats
            const currentUser = await oComponent.getCurrentUser();
            const authorizedSeats = await this.filterSpacesByDepartment(
                allSeatsInRoom,
                currentUser
            );

            // Mark favorite seats
            await this.markFavoriteSeats(authorizedSeats);

            // Process all authorized seats - use ALL bookings to show who has booked what times
            const seatsWithStatus = await Promise.all(
                authorizedSeats.map(async (seat: any) => {
                    // Get ALL bookings for this specific seat from the filtered bookings
                    const allSeatBookings = allTodaysBookings.filter(
                        (booking: any) => booking.seat?.id === seat.id
                    );

                    // Get user's bookings for this seat
                    const userSeatBookings = userTodaysBookings.filter(
                        (booking: any) => booking.seat?.id === seat.id
                    );

                    // Find user's primary booking for this seat (for backward compatibility)
                    const userBooking = userSeatBookings[0] || null;

                    // Check if the seat has any availability for the selected date
                    const isFullyBooked = isSeatFullyBookedForDay(allSeatBookings, selectedDate);

                    // Determine seat status for display
                    let status = "available";
                    if (isFullyBooked) {
                        status = "unavailable"; // Seat is completely booked
                    } else if (userSeatBookings.length > 0) {
                        status = "partial"; // User has bookings on this seat but still has availability
                    }

                    return {
                        ...seat,
                        available: !isFullyBooked, // Only available if not fully booked
                        bookedByUser: userSeatBookings.length > 0,
                        booking: userBooking,
                        status: status,
                        userBookings: userSeatBookings, // User's bookings for this seat
                        allBookings: allSeatBookings, // ALL bookings for this seat (from all users)
                        isFullyBooked: isFullyBooked
                    };
                })
            );

            // Filter out fully booked seats from FreeSpaces view
            const availableSeats = seatsWithStatus.filter((seat) => !seat.isFullyBooked);

            this.seatsModel.setProperty("/seats", availableSeats);
            this.seatsModel.setProperty(
                "/selectedDate",
                formatter.formatDate(formatter.formatLocalDateTime(selectedDate))
            );
        } catch (error) {
            console.error("Error fetching seats for room:", error);
            // Set empty data in case of error
            this.seatsModel.setProperty("/seats", []);
            MessageToast.show(this.getText("errorLoadingSeatsData"));
        }
    }

    private updateUrlWithCurrentDate(): void {
        // Get the current date from the date picker
        const datePicker = this.getView()?.byId("idSeatsDatePickerDatePicker") as DatePicker;
        const selectedDate = datePicker?.getDateValue();

        if (selectedDate && this.roomId) {
            // Format date as YYYY-MM-DD using local timezone
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
            const day = String(selectedDate.getDate()).padStart(2, "0");
            const dateString = `${year}-${month}-${day}`;

            // Update the URL hash without triggering navigation
            const newHash = `#freespaces/${this.roomId}/seats?date=${dateString}`;

            // Use replaceState to avoid adding to browser history
            window.history.replaceState(null, "", newHash);

            console.log("Updated FreeSpacesSeats URL with current date:", dateString);
        }
    }

    /**
     * Handle hash change events for browser navigation
     */
    private _onHashChange(): void {
        // Skip if we're currently updating from a hash change
        if (this.isUpdatingFromHashChange) {
            return;
        }

        console.log("Hash change detected in FreeSpacesSeats");

        // Extract date from URL and update date picker if different
        const urlParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
        const dateParam = urlParams.get("date");

        if (dateParam) {
            const datePicker = this.getView()?.byId("idSeatsDatePickerDatePicker") as DatePicker;
            if (datePicker) {
                try {
                    const [year, month, day] = dateParam.split("-").map(Number);
                    const urlDate = new Date(year, month - 1, day);
                    const currentDate = datePicker.getDateValue();

                    // Only update if dates are different
                    if (!currentDate || urlDate.getTime() !== currentDate.getTime()) {
                        this.isUpdatingFromHashChange = true;

                        datePicker.setDateValue(urlDate);
                        this.selectedDate = formatter.formatLocalDateTime(urlDate);
                        this.seatsModel.setProperty(
                            "/selectedDate",
                            formatter.formatDate(formatter.formatLocalDateTime(urlDate))
                        );

                        // Reload data with new date
                        this.fetchSeatsForRoom(this.roomId);

                        this.isUpdatingFromHashChange = false;
                    }
                } catch (error) {
                    console.error("Error parsing date from URL:", error);
                }
            }
        }
    }

    public onDatePickerChange(): void {
        // Update the selected date in the model with proper formatting
        const datePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
        if (datePicker) {
            const selectedDate = datePicker.getDateValue();
            if (selectedDate) {
                this.selectedDate = formatter.formatLocalDateTime(selectedDate);
                this.seatsModel.setProperty(
                    "/selectedDate",
                    formatter.formatDate(formatter.formatLocalDateTime(selectedDate))
                );
            }
        }

        // Clear existing data and show loading indicator while fetching new data
        this.seatsModel.setProperty("/seats", []);
        this.seatsModel.setProperty("/loading", true);

        // Update URL hash to reflect the selected date for sharing (only if not updating from hash change)
        if (!this.isUpdatingFromHashChange) {
            this.updateUrlWithCurrentDate();
        }

        // Refetch seats when date changes
        this.fetchSeatsForRoom(this.roomId)
            .catch((error) => {
                console.error("Error fetching seats after date change:", error);
            })
            .finally(() => {
                this.seatsModel.setProperty("/loading", false);
            });
    }

    public onShowOnlyFavoriteSeatsCheckBoxSelect(oEvent: Event): void {
        // Apply client-side filtering when favorite checkbox changes
        this.applyClientSideFilters();
    }

    public onFilterBarSearch(): void {
        this.applyClientSideFilters();
    }

    /**
     * Apply client-side filters to the table without making API requests
     */
    private applyClientSideFilters(): void {
        const aFilterItems = this._oFilterBar.getAllFilterItems(true) as FilterItem[];
        const aTableFilters = aFilterItems.reduce((aResult: Filter[], oFilterItem: FilterItem) => {
            const sFilterName = oFilterItem.getName();
            const oFilterControl = oFilterItem.getControl() as
                | DatePicker
                | ComboBox
                | SearchField
                | CheckBox;

            switch (sFilterName) {
                case "Seat": {
                    const control = oFilterControl as SearchField;
                    const sValue = control.getValue?.();
                    if (sValue) {
                        aResult.push(new Filter("name", FilterOperator.Contains, sValue));
                    }
                    break;
                }

                case "Position": {
                    const control = oFilterControl as SearchField;
                    const sValue = control.getValue?.();
                    if (sValue) {
                        aResult.push(new Filter("position", FilterOperator.Contains, sValue));
                    }
                    break;
                }

                case "Favorite": {
                    const control = oFilterControl as CheckBox;
                    const isSelected = control.getSelected?.();

                    if (isSelected) {
                        // Show only favorite seats using client-side filter
                        aResult.push(new Filter("isFavorite", FilterOperator.EQ, true));
                        console.log("Favorite filter applied: showing only favorites");
                    }
                    // If not selected, show all (no filter needed)
                    break;
                }
            }
            return aResult;
        }, []);

        const oTableBinding = this._oTable.getBinding("items") as ListBinding;
        oTableBinding.filter(aTableFilters);
    }

    public async onButtonBookPress(oEvent: Event): Promise<void> {
        const oSource = oEvent.getSource<Control>();
        const oContext = oSource.getBindingContext("SeatsModel");

        if (!oContext) {
            console.error("No binding context found");
            return;
        }

        const seatData = oContext.getObject() as any;

        // Use the seat data directly
        this.setDialogDataFromSeat(seatData);
        await this.openDeskDialog();
    }

    public onCloseBookingDetails(): void {
        if (this._dialog) {
            this._dialog.close();
            this._dialog.destroy();
            this._dialog = null;
        }
    }

    public onCloseButtonPress(): void {
        // Delegate to the existing close method
        this.onCloseBookingDetails();
    }

    public async onConfirmBooking(): Promise<void> {
        try {
            // Check if DialogData model exists (from new dialog)
            const DialogDataModel = this.getView().getModel("DialogData") as JSONModel;
            if (!DialogDataModel) {
                throw new Error(this.getText("noDialogDataForBooking"));
            }

            const dialogData = DialogDataModel.getData();

            // Get the selected date from the dialog data model (not from the main filter date picker)
            let selectedDate = dialogData.date;

            // Ensure we have a valid Date object
            if (!(selectedDate instanceof Date)) {
                console.warn("Invalid date in dialog data, using current date");
                selectedDate = new Date();
            }

            // Ensure we're using today or a future date
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                console.warn("Selected date is in the past, using today instead");
                selectedDate = new Date(); // Use today if selected date is in the past
            }

            // Determine time range based on mode (flexible vs fixed slots)
            const useFlexibleTime = dialogData.useFlexibleTime;
            let startTimeStr: string;
            let endTimeStr: string;

            if (useFlexibleTime) {
                // For flexible time mode, get times from startTime/endTime properties
                startTimeStr = dialogData.startTime || getCurrentTimePlusMinutes(1);
                endTimeStr = dialogData.endTime || "23:59";
            } else {
                // For regular time slots, parse from timeRange
                const timeRange = dialogData.timeRange;
                if (timeRange && timeRange.includes("-")) {
                    [startTimeStr, endTimeStr] = timeRange.split("-");
                }
            }

            // Validate and adjust start time if booking is for today and time has passed
            const adjustedStartTime = this.validateAndAdjustStartTime(startTimeStr, selectedDate);

            // Parse the time strings
            const [startHour, startMinute] = adjustedStartTime.split(":").map(Number);
            const [endHour, endMinute] = endTimeStr.split(":").map(Number);

            // Create booking times with validated start time
            const oStartDateTime = new Date(selectedDate);
            oStartDateTime.setHours(startHour, startMinute);

            const oEndDateTime = new Date(selectedDate);
            oEndDateTime.setHours(endHour, endMinute);

            // Log if time was adjusted
            if (adjustedStartTime !== startTimeStr) {
                console.log(
                    `Start time adjusted from ${startTimeStr} to ${adjustedStartTime} due to current time`
                );
            }

            // Format using existing formatter for ISO datetime
            const startTime = formatter.formatLocalDateTime(oStartDateTime);
            const endTime = formatter.formatLocalDateTime(oEndDateTime);

            console.log("Booking details:", {
                selectedDate: selectedDate.toISOString(),
                startTime: startTime,
                endTime: endTime
            });

            // Use the actual seat ID from dialog data
            const seatId = dialogData.seatId;
            if (!seatId) {
                throw new Error("No seat ID found for booking");
            }

            // Determine employeeId based on booking type
            let employeeId: string | null = null;

            // Check if booking for others
            if (dialogData.forOthers && dialogData.selectedEmployee) {
                // Use the stored employee ID if available
                if (this.selectedEmployeeId) {
                    employeeId = this.selectedEmployeeId;
                    console.log(
                        `Using stored employee ID ${employeeId} for ${dialogData.selectedEmployee}`
                    );
                } else {
                    // Fallback: Find the employee ID from the selected employee name
                    const employeesModel = this.getView().getModel("employees") as JSONModel;
                    if (employeesModel) {
                        const employees = employeesModel.getData()?.employees || [];
                        const selectedEmployeeName = dialogData.selectedEmployee;

                        // Find the employee by matching the display name format "LastName, FirstName"
                        const foundEmployee = employees.find((emp: any) => {
                            const displayName = `${emp.lastName}, ${emp.firstName}`;
                            return displayName === selectedEmployeeName;
                        });

                        if (foundEmployee) {
                            employeeId = foundEmployee.id;
                            console.log(
                                `Found employee ID ${employeeId} for ${selectedEmployeeName}`
                            );
                        } else {
                            console.warn(`Could not find employee ID for: ${selectedEmployeeName}`);
                            MessageToast.show(this.getText("selectedEmployeeNotFound"));
                        }
                    }
                }
            }
            // If not booking for others or no employee found, employeeId remains null (books for current user)

            const eventId = DEFAULT_EVENT_ID;
            const description = this.getText("bookingFromFreeSpaces");

            const attributes = {} as { [key: string]: number };
            if (dialogData.bringingChild) {
                attributes.AMOUNT_CHILDREN = 1;
            }
            if (dialogData.bringingDog) {
                attributes.AMOUNT_DOGS = 1;
            }

            console.log("Booking payload:", {
                seatId,
                startTime,
                endTime,
                employeeId,
                eventId,
                description,
                attributes
            });

            await this.apiService.booking.createBooking(
                seatId,
                startTime,
                endTime,
                employeeId,
                eventId,
                description,
                attributes
            );

            MessageToast.show(this.getText("bookingSuccessful"));

            // Close and destroy the dialog
            if (this._dialog) {
                this._dialog.close();
                this._dialog.destroy();
                this._dialog = null;
            }

            // Refresh the seats list to reflect the new booking
            await this.fetchSeatsForRoom(this.roomId);
        } catch (error) {
            console.error("Booking failed:", error);
            let errorMessage = this.getText("bookingFailed");

            if (error instanceof Error) {
                // Try to extract meaningful error message from backend
                if (error.message.includes("Das Datum muss entweder heute sein")) {
                    errorMessage = this.getText("bookingDateMustBeFuture");
                } else if (error.message.includes("booking not possible, already in use")) {
                    errorMessage = this.getText("seatAlreadyBooked");
                } else if (error.message.includes("event does not exist")) {
                    errorMessage = this.getText("invalidEventSelected");
                } else if (error.message.includes("seat does not exist")) {
                    errorMessage = this.getText("selectedSeatNoLongerExists");
                } else {
                    errorMessage = `${this.getText("bookingFailed")}: ${error.message}`;
                }
            }

            MessageToast.show(errorMessage);
        }
    }

    public async onButtonFavoritePress(oEvent: Event): Promise<void> {
        const oButton = oEvent.getSource<Button>();
        const oContext = oButton.getBindingContext("SeatsModel");

        if (!oContext) {
            return;
        }

        const seat = oContext.getObject() as { id: string; isFavorite?: boolean };

        // Prevent concurrent operations on the same seat
        if (this.processingFavorites.has(seat.id)) {
            MessageToast.show(this.getText("processingFavoriteOperation"));
            return;
        }

        try {
            // Mark operation as in progress
            this.processingFavorites.add(seat.id);

            if (seat.isFavorite) {
                await this.apiService.employee.removeFavoriteSeat(seat.id);
                seat.isFavorite = false;
                MessageToast.show(this.getText("removedFromFavorites"));
            } else {
                await this.apiService.employee.addFavoriteSeat(seat.id);
                seat.isFavorite = true;
                MessageToast.show(this.getText("addedToFavorites"));
            }

            oContext.getModel()?.refresh(true);
        } catch (error) {
            console.error("Error updating favorite seat:", error);

            // Provide more specific error messages based on error content
            const errorMessage = (error as Error)?.message;
            if (errorMessage?.includes("duplicate key")) {
                MessageToast.show("This seat is already in your favorites");
            } else if (errorMessage?.includes("constraint")) {
                MessageToast.show("Unable to update favorites - seat may already be favorited");
            } else {
                MessageToast.show(this.getText("failedToUpdateFavorite"));
            }
        } finally {
            // Always clean up the processing flag
            this.processingFavorites.delete(seat.id);
        }
    }

    public formatFavoriteIcon(isFavorite: boolean): string {
        const icons = this.getFavoriteIconMap();
        return icons[String(isFavorite)];
    }

    private getFavoriteIconMap(): Record<string, string> {
        return {
            true: "sap-icon://heart",
            false: "sap-icon://heart-2"
        };
    }

    // Formatters for booking status
    public formatBookingButtonText(status: string): string {
        switch (status) {
            case "booked":
            case "partial":
                return this.getText("bookSeat"); // Allow booking more time slots
            case "available":
                return this.getText("bookSeat");
            case "unavailable":
            default:
                return this.getText("notAvailable");
        }
    }

    public formatBookingButtonEnabled(status: string): boolean {
        // Always allow booking to enable multiple time slots per seat
        return status === "available" || status === "partial" || status === "booked";
    }

    public formatCancelButtonVisible(status: string): boolean {
        // Show cancel button if user has any bookings on this seat
        return status === "booked" || status === "partial";
    }

    public formatBookingButtonType(status: string): string {
        switch (status) {
            case "booked":
            case "partial":
                return "Emphasized"; // Emphasize seats with existing bookings
            case "available":
                return "Emphasized";
            case "unavailable":
            default:
                return "Default";
        }
    }

    private async markFavoriteSeats(seats?: Seat[]): Promise<void> {
        try {
            // Get favorite seats from API
            const favoriteSeatsResult = await this.apiService.employee.fetchFavoriteSeats();
            const favoriteSeats: any[] = Array.isArray(favoriteSeatsResult)
                ? favoriteSeatsResult
                : [];

            // Extract IDs into a Set for quick lookup
            const favoriteSeatIds = new Set(favoriteSeats.map((seat: { id: string }) => seat.id));

            // Use provided seats or get from model
            const seatsToUpdate = seats || this.seatsModel.getProperty("/seats") || [];

            if (!Array.isArray(seatsToUpdate)) {
                return;
            }

            // Mark seats as favorite if their id exists in the Set
            seatsToUpdate.forEach(
                (seat: { seatId?: string; id?: string; isFavorite?: boolean }) => {
                    const seatId = seat.seatId || seat.id;
                    seat.isFavorite = seatId ? favoriteSeatIds.has(seatId) : false;
                }
            );

            // Update the model if seats were provided
            if (seats) {
                this.seatsModel.setProperty("/seats", seatsToUpdate);
            } else {
                this.seatsModel.refresh(true);
            }

            console.log("Favorite seats marked:", seatsToUpdate);
        } catch (error) {
            console.error("Error marking favorite seats:", error);
        }
    }

    /**
     * Filters free spaces based on department rules and home zone authorization:
     * 1. Only show bookable rooms
     * 2. If room belongs to department, user must belong to same department
     * 3. If room has no department restriction, show to all users
     * 4. Exclude seats with name "ALL" as they are not bookable
     * 5. Only show seats that the user is authorized to access based on home zones
     */
    private async filterSpacesByDepartment(freeSpaces: Seat[], currentUser: any): Promise<Seat[]> {
        if (!freeSpaces || !Array.isArray(freeSpaces)) {
            return [];
        }

        const userDepartmentId = currentUser?.department?.id;

        // Get user's home zones for authorization check
        const authorizedSpaces = await HomeZoneAuthUtil.filterSpacesByHomeZone(
            freeSpaces,
            (space: Seat) => space?.room?.homeZones || []
        );

        return authorizedSpaces.filter((space: any) => {
            // Exclude seats with name "ALL" as they represent entire rooms and are not bookable
            if (space.name === "ALL") {
                return false;
            }

            // Exclude seats with empty or missing names as they are not properly configured
            if (!space.name || space.name.trim() === "") {
                return false;
            }

            // Check if room is bookable
            const isBookable = space.room?.bookable !== false;

            if (!isBookable) {
                return false;
            }

            // Check department restrictions
            const roomDepartmentId = space.room?.department?.id;

            // If room has no department restriction, show to all users
            if (!roomDepartmentId) {
                return true;
            }

            // If room has department restriction, user must belong to same department
            return userDepartmentId === roomDepartmentId;
        });
    }

    public onButtonBackPress(): void {
        this.navigateToFreeSpaces();
    }

    public onFreeSpacesLinkPress(): void {
        this.navigateToFreeSpaces();
    }

    private navigateToFreeSpaces(): void {
        // Get the current date from the date picker to preserve it in navigation
        const datePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
        const selectedDate = datePicker?.getDateValue();

        if (selectedDate) {
            // Navigate back with date parameter to preserve the filter using local timezone
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
            const day = String(selectedDate.getDate()).padStart(2, "0");
            const dateString = `${year}-${month}-${day}`;
            const hash = `#freespaces?date=${dateString}`;
            window.location.hash = hash;
        } else {
            this.navTo("FreeSpaces");
        }
    }

    // Methods copied from Graphic controller for dialog handling
    private setDialogDataFromSeat(seatData: Seat): void {
        const oDatePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
        const selectedDate = oDatePicker ? oDatePicker.getDateValue() : new Date();

        // Ensure we have a proper Date object
        let dateValue: Date;
        if (selectedDate instanceof Date) {
            dateValue = selectedDate;
        } else {
            dateValue = new Date(); // fallback to current date
        }

        // Ensure dateValue is not before today (minimum allowed date)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day

        if (dateValue < today) {
            dateValue = new Date(); // Use today if selected date is in the past
        }

        const isConferenceRoom = false;

        // Calculate available time ranges based on current time
        const availableTimeRanges = this.calculateAvailableTimeRanges(dateValue);

        // Filter time ranges based on ALL existing bookings for this seat (not just user's bookings)
        const allBookings = (seatData as any).allBookings || [];
        const filteredTimeRanges = filterTimeRangesByBookings(
            availableTimeRanges,
            allBookings,
            dateValue
        );

        const defaultTimeRange =
            filteredTimeRanges.length > 0 ? filteredTimeRanges[0].key : TIME_RANGES.FULL_DAY;

        // Only allow flexible time for regular seats if selected date is today and after business hours
        const useFlexibleTime = shouldUseFlexibleTime(dateValue, isConferenceRoom);

        // Set default start and end times for flexible time mode
        const defaultStartTime = useFlexibleTime ? getCurrentTimePlusMinutes(1) : "08:00";
        const defaultEndTime = useFlexibleTime ? "23:59" : "17:00";

        // Check if seat has existing bookings to show the view bookings button
        const hasExistingBookings =
            (seatData as any).allBookings && (seatData as any).allBookings.length > 0;
        console.log(seatData);

        const dialogData = {
            seatId: seatData.id, // Use id field (which is UUID) for booking API
            roomID: seatData.room?.id || this.roomId,
            seatID: seatData.name || seatData.id,
            room: seatData.room,
            floor: seatData.room?.floorPlan?.name || this.getText("groundFloor"),
            department: seatData.room?.description || this.getText("generalDepartment"),
            roomTypes: seatData.room?.roomTypes || [],
            roomRules: seatData.room?.roomRules || {},
            homeZones: seatData.room?.homeZones || [],
            position: seatData.position || "",
            orientation: seatData.orientation || "VERTICAL",
            isConferenceRoom: isConferenceRoom,
            useFlexibleTime: useFlexibleTime, // Flag to show time pickers for regular seats when after hours
            date: dateValue, // Use Date object for DatePicker
            minDate: new Date(today), // Create fresh Date object to prevent mutation
            maxDate: new Date(getMaxBookingDate()), // Create fresh Date object to prevent mutation
            timeRange: defaultTimeRange,
            availableTimeRanges: filteredTimeRanges, // Use filtered ranges
            startTime: defaultStartTime, // For flexible time selection
            endTime: defaultEndTime, // For flexible time selection
            forOthers: false,
            bringingDog: false,
            bringingChild: false,
            availabilityMessage: this.getText("dateAvailabilityChecking"),
            availabilityMessageType: "Information",
            showViewBookingsButton: hasExistingBookings
        };

        const RoomDialogModel = new JSONModel(dialogData);
        this.getView().setModel(RoomDialogModel, "DialogData");

        this.checkDateAvailability(dateValue);
    }

    public onDatePickerBookingDateChange(oEvent: Event) {
        const oDatePicker = oEvent.getSource() as DatePicker;
        const selectedDate = oDatePicker.getDateValue();

        if (selectedDate) {
            // Update available time ranges for the new date
            const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
            if (oDialogModel) {
                // Get seat data to access bookings for filtering
                const seatId = oDialogModel.getProperty("/seatId");
                const seatsModel = this.getView()?.getModel("SeatsModel") as JSONModel;
                const seats = seatsModel?.getProperty("/seats") || [];
                const currentSeat = seats.find((seat: any) => seat.id === seatId);
                const allBookings = currentSeat?.allBookings || [];

                const availableTimeRanges = this.calculateAvailableTimeRanges(selectedDate);

                // Filter time ranges based on existing bookings
                const filteredTimeRanges = filterTimeRangesByBookings(
                    availableTimeRanges,
                    allBookings,
                    selectedDate
                );

                oDialogModel.setProperty("/availableTimeRanges", filteredTimeRanges);

                // Check if we need to switch to/from flexible time mode
                const newDefaultTimeRange =
                    filteredTimeRanges.length > 0 &&
                    filteredTimeRanges[0].key !== "no-time-available"
                        ? filteredTimeRanges[0].key
                        : "no-time-available";

                // Apply same strict logic as in setDialogDataFromSeat
                let newUseFlexibleTime = false;
                const isConferenceRoom = oDialogModel.getProperty("/isConferenceRoom");
                if (!isConferenceRoom) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const bookingDate = new Date(selectedDate);
                    bookingDate.setHours(0, 0, 0, 0);
                    if (bookingDate.getTime() === today.getTime()) {
                        // Only after business hours (17:00)
                        const now = new Date();
                        const currentMinutes = now.getHours() * 60 + now.getMinutes();
                        const businessEnd =
                            parseInt(BUSINESS_HOURS.END.split(":")[0]) * 60 +
                            parseInt(BUSINESS_HOURS.END.split(":")[1]);
                        if (currentMinutes >= businessEnd) {
                            newUseFlexibleTime = true;
                        }
                    }
                    // If no standard time slots are available but bookings exist, force flexible time
                    if (newDefaultTimeRange === "no-time-available") {
                        newUseFlexibleTime = true;
                    }
                } else {
                    newUseFlexibleTime = true; // Always flexible for conference rooms
                }

                // Update flexible time mode and related properties
                oDialogModel.setProperty("/useFlexibleTime", newUseFlexibleTime);

                if (newUseFlexibleTime) {
                    // Switching to flexible time mode
                    oDialogModel.setProperty("/startTime", getCurrentTimePlusMinutes(1));
                    oDialogModel.setProperty("/endTime", "23:59");
                    oDialogModel.setProperty("/timeRange", "flexible-time");
                } else {
                    // Switching to standard time slots - reset to default values
                    oDialogModel.setProperty("/startTime", "08:00");
                    oDialogModel.setProperty("/endTime", "17:00");

                    // Set the first available time range or show no-time-available
                    oDialogModel.setProperty("/timeRange", newDefaultTimeRange);
                }
            }

            this.checkDateAvailability(selectedDate);
        }
    }

    public onTimeRangeChange(oEvent: Event) {
        // Get current date from dialog model
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        const selectedDate = oDialogModel.getProperty("/date");

        if (selectedDate) {
            this.checkDateAvailability(selectedDate);
        }
    }

    public onStartTimeChange(oEvent: Event): void {
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        if (oDialogModel) {
            let startTime = (oEvent.getSource() as any).getValue();
            const selectedDate = oDialogModel.getProperty("/date");
            let endTime = oDialogModel.getProperty("/endTime");

            // Validate and adjust start time if booking is for today
            startTime = validateAndAdjustStartTime(startTime, selectedDate);

            // If start time >= end time, set end time to start time + 1 hour
            if (isTimeGreaterOrEqual(startTime, endTime)) {
                endTime = calculateEndTimeFromStart(startTime);
                oDialogModel.setProperty("/endTime", endTime);
            }

            oDialogModel.setProperty("/startTime", startTime);

            // Re-check availability when time changes
            this.checkDateAvailability(selectedDate);
        }
    }

    public onEndTimeChange(oEvent: Event): void {
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        if (oDialogModel) {
            let endTime = (oEvent.getSource() as any).getValue();
            const startTime = oDialogModel.getProperty("/startTime");

            // If end time <= start time, set end time to start time + 1 hour
            if (isTimeGreaterOrEqual(startTime, endTime)) {
                endTime = calculateEndTimeFromStart(startTime);
            }

            oDialogModel.setProperty("/endTime", endTime);

            // Re-check availability when time changes
            const selectedDate = oDialogModel.getProperty("/date");
            this.checkDateAvailability(selectedDate);
        }
    }

    private async checkDateAvailability(selectedDate: Date) {
        const oDialogModel = this.getView()?.getModel("DialogData") as JSONModel;
        const oResourceBundle = (this.getView()?.getModel("i18n") as any)?.getResourceBundle();

        // Show checking message by setting model property for message strip
        oDialogModel.setProperty("/isCheckingAvailability", true);
        oDialogModel.setProperty("/availabilityMessageType", "Information");
        oDialogModel.setProperty(
            "/availabilityMessage",
            oResourceBundle.getText("dateAvailabilityChecking")
        );

        try {
            const useFlexibleTime = oDialogModel.getProperty("/useFlexibleTime");
            let startTimeStr: string;
            let endTimeStr: string;

            if (useFlexibleTime) {
                // For flexible time mode, get times from startTime/endTime properties
                startTimeStr =
                    oDialogModel.getProperty("/startTime") || getCurrentTimePlusMinutes(1);
                endTimeStr = oDialogModel.getProperty("/endTime") || "23:59";
            } else {
                // For regular time slots, parse from timeRange
                const timeRange = oDialogModel.getProperty("/timeRange") || "08:00-17:00";
                if (timeRange.includes("-")) {
                    [startTimeStr, endTimeStr] = timeRange.split("-");
                } else {
                    // Fallback for invalid timeRange
                    startTimeStr = "08:00";
                    endTimeStr = "17:00";
                }
            }

            // Validate and adjust start time if booking is for today and time has passed
            const adjustedStartTime = this.validateAndAdjustStartTime(startTimeStr, selectedDate);

            // Format date strings for API
            const startDate = new Date(selectedDate);
            const endDate = new Date(selectedDate);

            startDate.setHours(
                parseInt(adjustedStartTime.split(":")[0]),
                parseInt(adjustedStartTime.split(":")[1]) || 0,
                0,
                0
            );
            endDate.setHours(
                parseInt(endTimeStr.split(":")[0]),
                parseInt(endTimeStr.split(":")[1]) || 0,
                0,
                0
            );

            const startISO = formatter.formatLocalDateTime(startDate);
            const endISO = formatter.formatLocalDateTime(endDate);

            // Update the dialog model with adjusted time if it changed
            if (useFlexibleTime && adjustedStartTime !== startTimeStr) {
                oDialogModel.setProperty("/startTime", adjustedStartTime);
                console.log(`Start time adjusted to: ${adjustedStartTime}`);
            } else if (!useFlexibleTime && adjustedStartTime !== startTimeStr) {
                const adjustedTimeRange = `${adjustedStartTime}-${endTimeStr}`;
                oDialogModel.setProperty("/timeRange", adjustedTimeRange);
                console.log(`Time range adjusted to: ${adjustedTimeRange}`);
            }

            const seatID = oDialogModel.getProperty("/seatId");

            // Use the API service method instead of direct fetch
            const result = await this.apiService.seat.checkSeatAvailability(
                seatID,
                startISO,
                endISO
            );

            // Clear busy state and show result
            oDialogModel.setProperty("/isCheckingAvailability", false);

            oDialogModel.setData({
                ...oDialogModel.getData(),
                availabilityMessage: result.available
                    ? oResourceBundle.getText("dateAvailable")
                    : oResourceBundle.getText("seatAlreadyBooked"),
                availabilityMessageType: result.available ? "Success" : "Error"
            });
        } catch (error) {
            console.error("Error checking date availability:", error);
            // Clear busy state and show error
            oDialogModel.setProperty("/isCheckingAvailability", false);

            oDialogModel.setData({
                ...oDialogModel.getData(),
                availabilityMessage: oResourceBundle.getText("dateAvailabilityError"),
                availabilityMessageType: "Error"
            });
        }
    }

    private setDialogData(data: Room | Seat, isRoom: boolean): void {
        const oDatePicker = this.getView().byId("idSeatsDatePickerDatePicker") as DatePicker;
        const selectedDate = oDatePicker ? oDatePicker.getDateValue() : new Date();

        // Ensure we have a proper Date object
        let dateValue: Date;
        if (selectedDate instanceof Date) {
            dateValue = selectedDate;
        } else {
            dateValue = new Date(); // fallback to current date
        }

        // Get room info from the room model or data
        const selectedRoom = this.roomModel?.getProperty("/selectedRoom") as Room;

        const dialogData = {
            roomID: isRoom ? (data as Room).id : (data as Seat).name.split("-")[0],
            seatID: isRoom ? "N/A" : (data as Seat).name.split("-")[1],
            floor:
                (data as Room).floorPlan.name ||
                selectedRoom?.floorPlan.name ||
                this.getText("groundFloor"),
            department:
                (data as Room).description ||
                selectedRoom?.description ||
                this.getText("generalDepartment"),
            date: dateValue, // Use Date object for DatePicker
            timeRange: TIME_RANGES.FULL_DAY,
            forOthers: false,
            bringingDog: false,
            bringingChild: false,
            availabilityMessage: this.getText("dateAvailabilityChecking"),
            availabilityMessageType: "Information"
        };

        const RoomDialogModel = new JSONModel(dialogData);
        this.getView().setModel(RoomDialogModel, "DialogData");
    }

    private async openDeskDialog(): Promise<void> {
        try {
            // Load employee data first
            await this.loadEmployeeData();

            // Close existing dialog if it exists
            if (this._dialog) {
                this._dialog.close();
                this._dialog.destroy();
                this._dialog = null;
            }

            // Load the Dialog fragment
            const oFragment = await Fragment.load({
                name: "de.realcore.realseats.view.fragment.SeatBookingDialog",
                controller: this,
                id: this.getView().getId() + "--seatBookingDialog--" + Date.now()
            });

            if (!oFragment) {
                console.error("Fragment not loaded");
                return;
            }

            // Get the Dialog from the fragment
            let dialog: Dialog;
            if (Array.isArray(oFragment)) {
                dialog = oFragment[0] as Dialog;
            } else {
                dialog = oFragment as Dialog;
            }

            if (!dialog || !(dialog instanceof Dialog)) {
                console.error("Fragment content is not a Dialog");
                return;
            }

            // Store dialog reference
            this._dialog = dialog;

            // Add the dialog to the view
            this.getView().addDependent(dialog);

            // Ensure the dialog has the employee model
            const employeeModel = this.getView().getModel("employees");
            if (employeeModel) {
                dialog.setModel(employeeModel, "employees");
            }

            // Open the dialog
            dialog.open();
        } catch (error) {
            console.error("Failed to load the dialog fragment.", error);
        }
    }

    public async onSaveAsFavorites(): Promise<void> {
        const RoomDialogModel = this.getView().getModel("DialogData") as JSONModel;
        const deskData = RoomDialogModel.getData();

        try {
            // Use the actual seat ID from dialog data
            const seatId = deskData.seatId || deskData.roomID;

            // Prevent concurrent operations on the same seat
            if (this.processingFavorites.has(seatId)) {
                MessageToast.show(this.getText("processingFavoriteOperation"));
                return;
            }

            try {
                // Mark operation as in progress
                this.processingFavorites.add(seatId);

                await this.apiService.employee.addFavoriteSeat(seatId);

                // Show success message
                const oResourceBundle = await this.getResourceBundle();
                MessageToast.show(
                    oResourceBundle.getText("deskSavedAsFavorite", [
                        deskData.roomID,
                        deskData.seatID
                    ])
                );
            } finally {
                // Always clean up the processing flag
                this.processingFavorites.delete(seatId);
            }
        } catch (error) {
            console.error("Error saving seat as favorite:", error);

            // Provide more specific error messages based on error content
            const errorMessage = (error as Error)?.message;
            if (errorMessage?.includes("duplicate key")) {
                MessageToast.show(this.getText("seatAlreadyInFavorites"));
            } else if (errorMessage?.includes("constraint")) {
                MessageToast.show(this.getText("unableToUpdateFavorites"));
            } else {
                MessageToast.show(this.getText("failedToUpdateFavorite"));
            }
        }
    }

    public onBookingOnBehalfChange(oEvent: Event): void {
        const checkBox = oEvent.getSource<CheckBox>();
        const bSelected = checkBox.getSelected();
        const RoomDialogModel = this.getView().getModel("DialogData") as JSONModel;
        RoomDialogModel.setProperty("/forOthers", bSelected);
    }

    public onCancelBooking(oEvent: Event): void {
        const oButton = oEvent.getSource<Button>();
        const oContext = oButton.getBindingContext("SeatsModel");
        const oView = this.getView();

        if (!oContext) {
            console.error("No binding context found for cancel booking");
            return;
        }

        const seatData = oContext.getObject() as any;
        const booking = seatData.booking;

        if (!booking?.id) {
            MessageToast.show(this.getText("noBookingFoundToCancel"));
            return;
        }

        // Get room name from the room model
        const selectedRoom = this.roomModel?.getProperty("/selectedRoom");
        const roomName = selectedRoom?.name || this.getText("unknownRoom");

        // Create a temporary model for the dialog with booking data
        const dialogData = {
            id: booking.id,
            seat: {
                name: seatData.name || seatData.position || `Seat ${seatData.id}`,
                room: {
                    name: roomName
                }
            },
            startTime: booking.startTime,
            endTime: booking.endTime,
            bookingUser: booking.bookingUser || {
                firstName: this.getText("currentUser"),
                lastName: ""
            },
            cancelled: false,
            cancelDescription: "",
            canceledDesc: ""
        };

        const dialogModel = new JSONModel(dialogData);

        console.log("Cancel dialog data:", dialogModel.getData());
        console.log("Seat data structure:", JSON.stringify(seatData, null, 2));
        console.log("Booking data structure:", JSON.stringify(booking, null, 2));
        console.log("Selected room data:", JSON.stringify(selectedRoom, null, 2));

        if (!this.cancelDialog) {
            Fragment.load({
                id: "freeSpacesSeats",
                name: "de.realcore.realseats.view.fragment.CancelBookingDialog",
                controller: this
            })
                .then((oFragment) => {
                    if (!oFragment) {
                        console.error("Fragment not loaded");
                        return;
                    }

                    this.cancelDialog = oFragment as Dialog;
                    // Set the model BEFORE adding to view
                    this.cancelDialog.setModel(dialogModel, "BookingsModel");
                    // Also try setting a binding context like MyBookings does
                    const context = dialogModel.createBindingContext("/");
                    this.cancelDialog.setBindingContext(context, "BookingsModel");
                    oView.addDependent(this.cancelDialog);
                    // Force a model refresh
                    this.cancelDialog.getModel("BookingsModel")?.refresh();

                    console.log("Opening cancel dialog with data:", {
                        roomName: dialogModel.getProperty("/seat/room/name"),
                        seatName: dialogModel.getProperty("/seat/name"),
                        startTime: dialogModel.getProperty("/startTime"),
                        endTime: dialogModel.getProperty("/endTime")
                    });

                    this.cancelDialog.open();
                })
                .catch((error) => {
                    console.error("Failed to load the CancelBookingDialog fragment", error);
                });
        } else {
            this.cancelDialog.setModel(dialogModel, "BookingsModel");
            // Also update the binding context for reuse
            const context = dialogModel.createBindingContext("/");
            this.cancelDialog.setBindingContext(context, "BookingsModel");
            // Force a model refresh to ensure UI updates
            this.cancelDialog.getModel("BookingsModel")?.refresh();
            console.log("Reusing cancel dialog with new data:", {
                roomName: dialogModel.getProperty("/seat/room/name"),
                seatName: dialogModel.getProperty("/seat/name"),
                startTime: dialogModel.getProperty("/startTime"),
                endTime: dialogModel.getProperty("/endTime")
            });
            this.cancelDialog.open();
        }
    }

    public onCancelDialog(): void {
        if (this.cancelDialog) {
            this.cancelDialog.close();
        }
    }

    public async onConfirmCancellation(): Promise<void> {
        const oDialog = this.cancelDialog;
        const dialogModel = oDialog?.getModel("BookingsModel") as JSONModel;
        const bookingData = dialogModel?.getData();

        if (!bookingData?.id) {
            MessageBox.error(this.getText("noBookingIdForCancellation"));
            return;
        }

        try {
            const cancelDescription =
                bookingData.cancelDescription || this.getText("cancelledFromSeatOverview");

            await this.apiService.booking.cancelBooking(bookingData.id, cancelDescription);

            MessageBox.success(this.getText("bookingCanceledSuccessfully"));
            this.cancelDialog?.close();

            // Refresh the seats list to reflect the cancellation
            await this.fetchSeatsForRoom(this.roomId);
        } catch (error) {
            console.error("Cancel booking failed:", error);
            let errorMessage = this.getText("errorCancelingBooking");

            if (error instanceof Error) {
                errorMessage += ": " + error.message;
            }

            MessageBox.error(errorMessage);
        }
    }

    public onExit(): void {
        // Clean up dialog instances to prevent memory leaks
        if (this._dialog) {
            this._dialog.destroy();
            this._dialog = null;
        }
        if (this._employeeDialog) {
            this._employeeDialog.destroy();
            this._employeeDialog = null;
        }
        if (this.cancelDialog) {
            this.cancelDialog.destroy();
        }
        if (this._bookingsPopover) {
            this._bookingsPopover.destroy();
            this._bookingsPopover = null;
        }
    }

    /**
     * Employee input handlers for SeatBookingDialog fragment
     */
    public onSelectedEmployeeInputValueHelpRequest(): void {
        // Open employee value help dialog
        this.openEmployeeValueHelpDialog();
    }

    private async openEmployeeValueHelpDialog(): Promise<void> {
        // Load and open employee selection dialog using SelectDialog
        if (!this._employeeDialog) {
            const oSelectDialog = (await Fragment.load({
                name: "de.realcore.realseats.view.fragment.EmployeeValueHelp",
                controller: this,
                id: this.getView().getId() + "--employeeValueHelpDialog"
            })) as Dialog;

            this._employeeDialog = oSelectDialog;
            this.getView().addDependent(this._employeeDialog);
        }

        // Ensure the dialog has access to the employees model (already loaded)
        const employeesModel = this.getView().getModel("employees");
        console.log("Employee model data:", (employeesModel as JSONModel)?.getData());
        if (employeesModel && this._employeeDialog) {
            this._employeeDialog.setModel(employeesModel, "employees");
            console.log("Model set on dialog successfully");
        }

        // Reset selection
        this.selectedEmployeeFromDialog = null;

        this._employeeDialog.open();
        console.log("Dialog opened");
    }

    public onAllEmployeesSelectDialogSearch(oEvent: Event): void {
        const sValue = ((oEvent as any).getParameter("value") as string) || "";
        const oFilter = new Filter({
            filters: [
                new Filter("firstName", FilterOperator.Contains, sValue),
                new Filter("lastName", FilterOperator.Contains, sValue),
                new Filter("department/name", FilterOperator.Contains, sValue)
            ],
            and: false
        });

        const oSelectDialog = oEvent.getSource() as SelectDialog;
        const oBinding = oSelectDialog.getBinding("items") as ListBinding;
        oBinding.filter(sValue ? [oFilter] : []);
    }

    public onAllEmployeesSelectDialogLiveChange(oEvent: Event): void {
        // Reuse the same search logic for live change
        this.onAllEmployeesSelectDialogSearch(oEvent);
    }

    public onAllEmployeesSelectDialogConfirm(oEvent: Event): void {
        const oSelectedItem = (oEvent as any).getParameter("selectedItem") as StandardListItem;
        if (oSelectedItem) {
            const oContext = oSelectedItem.getBindingContext("employees");
            if (oContext) {
                const oEmployee = oContext.getObject() as any;
                this.selectedEmployeeFromDialog = `${oEmployee.lastName}, ${oEmployee.firstName}`;
                this.selectedEmployeeId = oEmployee.id;

                // Update the input field in the main dialog
                const dialogModel = this.getView().getModel("DialogData") as JSONModel;
                dialogModel.setProperty("/selectedEmployee", this.selectedEmployeeFromDialog);

                console.log("Selected employee:", this.selectedEmployeeFromDialog);
            }
        }

        // Clean up
        if (this._employeeDialog) {
            this._employeeDialog.destroy();
            this._employeeDialog = null;
        }
    }

    public onAllEmployeesSelectDialogCancel(): void {
        // Clean up
        if (this._employeeDialog) {
            this._employeeDialog.destroy();
            this._employeeDialog = null;
        }
    }

    private findUnifiedEmployeeList(): List | null {
        if (!this._employeeDialog) {
            return null;
        }

        // Try direct fragment lookup first with the new ID
        let unifiedList = Fragment.byId(this._employeeDialog.getId(), "idAllEmployeesList") as List;

        // If direct lookup fails, search recursively
        if (!unifiedList) {
            const found = this.searchUnifiedListRecursively(this._employeeDialog.getContent());
            unifiedList = found;
        }

        return unifiedList;
    }

    private searchUnifiedListRecursively(controls: any[]): List | null {
        for (const control of controls) {
            if (control instanceof List) {
                if (control.getId().includes("idAllEmployeesList")) {
                    return control;
                }
            } else if (control.getContent) {
                const found = this.searchInNestedControlsForUnified(control);
                if (found) {
                    return found;
                }
            }
        }

        return null;
    }

    private searchInNestedControlsForUnified(control: any): List | null {
        try {
            const nestedControls = control.getContent ? control.getContent() : [];
            if (Array.isArray(nestedControls)) {
                return this.searchUnifiedListRecursively(nestedControls);
            } else if (nestedControls?.getContent) {
                return this.searchInNestedControlsForUnified(nestedControls);
            }
        } catch (error) {
            console.warn("Error searching nested controls:", error);
        }

        return null;
    }

    private applyFiltersToUnifiedEmployeeList(unifiedList: List | null, aFilters: Filter[]): void {
        try {
            // Apply filters to unified list
            if (unifiedList) {
                const binding = unifiedList.getBinding("items") as ListBinding;
                if (binding) {
                    binding.filter(aFilters);
                }
            }
        } catch (error) {
            console.warn("Error applying filters to unified employee list:", error);
        }
    }

    private createEmployeeSearchFilters(sQuery: string): Filter[] {
        const aFilters: Filter[] = [];
        if (sQuery) {
            const oFilter = new Filter({
                filters: [
                    new Filter("firstName", FilterOperator.Contains, sQuery),
                    new Filter("lastName", FilterOperator.Contains, sQuery),
                    new Filter("department/name", FilterOperator.Contains, sQuery)
                ],
                and: false
            });
            aFilters.push(oFilter);
        }
        return aFilters;
    }

    public onEmployeeSelectionChange(oEvent: Event): void {
        const oSelectedItem = (oEvent as any).getParameter("listItem") as StandardListItem;
        if (oSelectedItem) {
            this.selectedEmployeeFromDialog = oSelectedItem.getTitle();
        }
    }

    public onStandardListItemEmployeePress(oEvent: Event): void {
        if (!this._employeeDialog) {
            console.warn(this.getText("employeeDialogNotInitialized"));
            return;
        }

        console.log("Item pressed, dialog ID:", this._employeeDialog.getId());

        const oItem = oEvent.getSource() as StandardListItem;
        console.log("Clicked item:", oItem.getTitle());

        // Get the employee data from the binding context
        const oContext = oItem.getBindingContext("employees");
        if (oContext) {
            const oEmployee = oContext.getObject() as any;
            console.log("Selected employee data:", oEmployee);

            // Store both display name and employee ID
            this.selectedEmployeeFromDialog = `${oEmployee.lastName}, ${oEmployee.firstName}`;
            this.selectedEmployeeId = oEmployee.id;

            console.log("Selected employee:", this.selectedEmployeeFromDialog);
            console.log("Employee ID:", this.selectedEmployeeId);
        }

        // Try to find the list using the new unified list ID
        let oList = Fragment.byId(this._employeeDialog.getId(), "idAllEmployeesList") as List;

        if (!oList) {
            console.log("List not found via Fragment.byId, trying alternative approach");
            // Try to find the list by traversing from the item
            let parent = oItem.getParent();
            while (parent && !(parent instanceof List)) {
                parent = parent.getParent();
            }
            if (parent instanceof List) {
                oList = parent;
                console.log("Found list via parent traversal:", oList.getId());
            }
        }

        if (oList) {
            // Set selection
            oList.setSelectedItem(oItem);
        }

        // Auto-confirm on item press
        this.onEmployeeDialogConfirm();
    }

    public onEmployeeDialogConfirm(): void {
        if (this.selectedEmployeeFromDialog) {
            console.log("Confirming employee selection:", this.selectedEmployeeFromDialog);

            // Update the dialog data model
            const DialogModel = this.getView().getModel("DialogData") as JSONModel;
            if (DialogModel) {
                DialogModel.setProperty("/selectedEmployee", this.selectedEmployeeFromDialog);
                console.log("Updated DialogData model");
            } else {
                console.warn("DialogData model not found");
            }

            // Update the input field directly if the main dialog exists
            if (this._dialog) {
                const oInput = Fragment.byId(
                    this._dialog.getId(),
                    "idSelectedEmployeeInput"
                ) as Input;
                if (oInput) {
                    oInput.setValue(this.selectedEmployeeFromDialog);
                    console.log("Updated input field value");
                } else {
                    console.warn("Employee input field not found in main dialog");
                }
            } else {
                console.warn("Main dialog reference not found");
            }

            MessageToast.show(this.getText("selectedEmployee", [this.selectedEmployeeFromDialog]));
        } else {
            console.warn(this.getText("noEmployeeSelected"));
        }

        if (this._employeeDialog) {
            this._employeeDialog.close();
        }
    }

    public onEmployeeDialogCancel(): void {
        if (this._employeeDialog) {
            this._employeeDialog.close();
        }
    }

    public onSelectedEmployeeInputSuggest(oEvent: Event): void {
        // Handle suggestion filtering
        const sValue = (
            ((oEvent as any).getParameter("suggestValue") as string) || ""
        ).toLowerCase();

        const oInput = oEvent.getSource();
        const aFilters: Filter[] = [];

        if (sValue) {
            const oFilter = new Filter({
                filters: [
                    new Filter("lastName", FilterOperator.Contains, sValue),
                    new Filter("firstName", FilterOperator.Contains, sValue),
                    new Filter("department/name", FilterOperator.Contains, sValue)
                ],
                and: false
            });
            aFilters.push(oFilter);
        }

        const oBinding = (oInput as any).getBinding("suggestionItems") as ListBinding;
        if (oBinding) {
            oBinding.filter(aFilters);
        }
    }

    public onSelectedEmployeeInputSuggestionItemSelected(oEvent: Event): void {
        // Handle when user selects an employee from suggestions
        const oSelectedItem = (oEvent as any).getParameter("selectedItem");
        if (oSelectedItem) {
            const sSelectedText = oSelectedItem.getText();
            const DialogModel = this.getView().getModel("DialogData") as JSONModel;
            if (DialogModel) {
                DialogModel.setProperty("/selectedEmployee", sSelectedText);
            }
        }
    }

    private async loadEmployeeData(): Promise<void> {
        // Load employee data from API for the SeatBookingDialog fragment
        try {
            const employeeData = await this.apiService.employee.fetchEmployeesWithSuggestions();

            console.log("Raw employee data:", employeeData);
            console.log("Suggested employees count:", employeeData.suggestedEmployees?.length || 0);
            console.log("All employees count:", employeeData.employees?.length || 0);

            // Create a unified list with recent employees first
            const allEmployees: any[] = [];

            // Add suggested employees first with isRecent flag
            if (employeeData.suggestedEmployees) {
                employeeData.suggestedEmployees.forEach((emp: any) => {
                    allEmployees.push({ ...emp, isRecent: true });
                });
            }

            // Add remaining employees (not suggested) with isRecent false
            if (employeeData.employees) {
                employeeData.employees.forEach((emp: any) => {
                    // Only add if not already in suggested list
                    if (
                        !employeeData.suggestedEmployees?.some(
                            (suggested: any) => suggested.id === emp.id
                        )
                    ) {
                        allEmployees.push({ ...emp, isRecent: false });
                    }
                });
            }

            console.log("Unified allEmployees count:", allEmployees.length);
            console.log("First few allEmployees:", allEmployees.slice(0, 3));

            const unifiedEmployeeModel = new JSONModel({
                employees: employeeData.employees || [],
                suggestedEmployees: employeeData.suggestedEmployees || [],
                allEmployees: allEmployees
            });

            this.getView().setModel(unifiedEmployeeModel, "employees");
            this.getView().setModel(unifiedEmployeeModel, "EmployeeModel");
        } catch (error) {
            console.error("Error loading employee data:", error);
            const fallbackModel = new JSONModel({
                employees: [],
                suggestedEmployees: [],
                allEmployees: []
            });
            this.getView().setModel(fallbackModel, "employees");
        }
    }

    /**
     * Calculates available time ranges based on current time for today's bookings
     */
    private calculateAvailableTimeRanges(selectedDate: Date): Array<{ key: string; text: string }> {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const bookingDate = new Date(selectedDate);
        bookingDate.setHours(0, 0, 0, 0);

        // If not today, show all standard options with FULL_DAY first
        if (bookingDate.getTime() !== today.getTime()) {
            return [
                { key: TIME_RANGES.FULL_DAY, text: TIME_RANGES.FULL_DAY },
                { key: TIME_RANGES.MORNING, text: TIME_RANGES.MORNING },
                { key: TIME_RANGES.AFTERNOON, text: TIME_RANGES.AFTERNOON }
            ];
        }

        // For today, calculate what's still possible
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTotalMinutes = currentHour * 60 + currentMinute;

        const availableRanges: Array<{ key: string; text: string }> = [];

        // Parse business hours for calculations
        const morningEnd = parseInt(BUSINESS_HOURS.LUNCH_START.split(":")[0]) * 60;
        const afternoonStart = parseInt(BUSINESS_HOURS.LUNCH_END.split(":")[0]) * 60;
        const dayStart = parseInt(BUSINESS_HOURS.START.split(":")[0]) * 60;
        const dayEnd = parseInt(BUSINESS_HOURS.END.split(":")[0]) * 60;

        // Full day slot - prioritize this option first
        if (currentTotalMinutes < dayStart) {
            // Before business hours, show standard full day slot
            availableRanges.push({ key: TIME_RANGES.FULL_DAY, text: TIME_RANGES.FULL_DAY });
        } else if (currentTotalMinutes < dayEnd) {
            // If we're within business hours, offer a "remaining day" option first
            const startTime = getCurrentTimePlusMinutes(1);
            availableRanges.push({
                key: `${startTime}-${BUSINESS_HOURS.END}`,
                text: `${startTime}-${BUSINESS_HOURS.END}`
            });
        }

        // Morning slot, available if current time is before lunch
        if (currentTotalMinutes < morningEnd) {
            if (currentTotalMinutes < dayStart) {
                // Before business hours, show standard morning slot
                availableRanges.push({ key: TIME_RANGES.MORNING, text: TIME_RANGES.MORNING });
            } else {
                // During morning hours, show adjusted morning slot
                const startTime = getCurrentTimePlusMinutes(1);
                availableRanges.push({
                    key: `${startTime}-${BUSINESS_HOURS.LUNCH_START}`,
                    text: `${startTime}-${BUSINESS_HOURS.LUNCH_START}`
                });
            }
        }

        // Afternoon slot, available if current time is before end of day
        if (currentTotalMinutes < dayEnd) {
            if (currentTotalMinutes < afternoonStart) {
                // Before afternoon, show standard afternoon slot
                availableRanges.push({ key: TIME_RANGES.AFTERNOON, text: TIME_RANGES.AFTERNOON });
            } else {
                // During afternoon, show adjusted afternoon slot
                const startTime = getCurrentTimePlusMinutes(1);
                availableRanges.push({
                    key: `${startTime}-${BUSINESS_HOURS.END}`,
                    text: `${startTime}-${BUSINESS_HOURS.END}`
                });
            }
        }

        // If no ranges are available (after business hours), enable flexible time selection
        if (availableRanges.length === 0) {
            availableRanges.push({ key: "flexible-time", text: "Flexible time selection" });
        }

        return availableRanges;
    }

    /**
     * Validates and adjusts start time based on current time if booking is for today
     */
    private validateAndAdjustStartTime(startTime: string, selectedDate: Date): string {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const bookingDate = new Date(selectedDate);
        bookingDate.setHours(0, 0, 0, 0);

        // If booking is for today, ensure start time is not in the past
        if (bookingDate.getTime() === today.getTime()) {
            const currentTimePlusOne = getCurrentTimePlusMinutes(1);
            const [currentHour, currentMinute] = currentTimePlusOne.split(":").map(Number);
            const [startHour, startMinute] = startTime.split(":").map(Number);

            const currentTotalMinutes = currentHour * 60 + currentMinute;
            const startTotalMinutes = startHour * 60 + startMinute;

            if (startTotalMinutes < currentTotalMinutes) {
                return currentTimePlusOne;
            }
        }

        return startTime;
    }

    /**
     * Shows a popover with all bookings for a seat when the link is clicked
     * @param oEvent The press event
     */
    public async onLinkBookingsPress(oEvent: Event): Promise<void> {
        const oLink = oEvent.getSource() as Control;
        const oContext = oLink.getBindingContext("SeatsModel");

        if (!oContext) {
            return;
        }

        const oSeat = oContext.getObject() as any;
        const aAllBookings = oSeat.allBookings || [];

        // Get current user employee ID from existing bookings data
        // (since getCurrentUser only returns name, we need to get ID from bookings)
        let currentUserId: string | undefined;
        const userBookings = oSeat.userBookings || [];
        if (userBookings.length > 0) {
            currentUserId = userBookings[0].employee?.id;
        }

        // Format bookings to match RoomTypesPopover structure with cancellation info
        const formattedBookings = aAllBookings.map((booking: any) => ({
            id: booking.id,
            bookerName:
                `${booking.employee?.firstName || ""} ${booking.employee?.lastName || ""}`.trim() ||
                "Unknown User",
            timeSlot: `${new Date(booking.startTime).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} - ${new Date(booking.endTime).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`,
            canCancel:
                currentUserId &&
                (booking.employee?.id === currentUserId ||
                    booking.bookingUser?.id === currentUserId),
            cancelled: booking.cancelled || false,
            startTime: booking.startTime,
            endTime: booking.endTime,
            bookingUser: booking.bookingUser
        }));

        // Check if there are any bookings that can be deleted
        const hasAnyDeletableBookings = formattedBookings.some(
            (booking: any) => booking.canCancel && !booking.cancelled
        );

        // Create popover if it doesn't exist
        if (!this._bookingsPopover) {
            this._bookingsPopover = (await Fragment.load({
                id: this.getView()?.getId(),
                name: "de.realcore.realseats.view.fragment.SeatBookingsPopover",
                controller: this
            })) as any;
            this.getView()?.addDependent(this._bookingsPopover);
        }

        // Set the bookings data to the popover
        const oPopoverModel = new JSONModel({
            allBookings: formattedBookings,
            hasAnyDeletableBookings: hasAnyDeletableBookings
        });
        this._bookingsPopover.setModel(oPopoverModel, "PopoverBookingsModel");

        // Open popover
        this._bookingsPopover.openBy(oLink);
    }

    /**
     * Handle delete from popover list - opens cancellation dialog
     */
    public onAllBookingsListDelete(oEvent: Event): void {
        const oListItem = (oEvent as any).getParameter("listItem");
        const oContext = oListItem.getBindingContext("PopoverBookingsModel");

        if (!oContext) {
            MessageToast.show("Could not find booking to cancel");
            return;
        }

        const booking = oContext.getObject();

        if (!booking?.id) {
            MessageToast.show("No booking ID found for cancellation");
            return;
        }

        // Check if user has permission to cancel this booking
        if (!booking.canCancel || booking.cancelled) {
            MessageToast.show("You don't have permission to cancel this booking");
            return;
        }

        // Close the popover first
        if (this._bookingsPopover) {
            this._bookingsPopover.close();
        }

        // Get room name from the room model
        const selectedRoom = this.roomModel?.getProperty("/selectedRoom");
        const roomName = selectedRoom?.name || this.getText("unknownRoom");

        // Create dialog data using the booking from popover
        const dialogData = {
            id: booking.id,
            seat: {
                name: booking.bookerName || `Seat ${booking.id}`,
                room: {
                    name: roomName
                }
            },
            startTime: booking.startTime,
            endTime: booking.endTime,
            bookingUser: booking.bookingUser || {
                firstName: booking.bookerName?.split(" ")[0] || "Unknown",
                lastName: booking.bookerName?.split(" ")[1] || "User"
            },
            cancelled: false,
            cancelDescription: "",
            canceledDesc: ""
        };

        const dialogModel = new JSONModel(dialogData);

        // Open the existing cancellation dialog
        if (!this.cancelDialog) {
            Fragment.load({
                id: "freeSpacesSeats",
                name: "de.realcore.realseats.view.fragment.CancelBookingDialog",
                controller: this
            })
                .then((oFragment) => {
                    if (!oFragment) {
                        console.error("Fragment not loaded");
                        return;
                    }

                    this.cancelDialog = oFragment as Dialog;
                    this.cancelDialog.setModel(dialogModel, "BookingsModel");
                    const context = dialogModel.createBindingContext("/");
                    this.cancelDialog.setBindingContext(context, "BookingsModel");
                    this.getView().addDependent(this.cancelDialog);
                    this.cancelDialog.getModel("BookingsModel")?.refresh();
                    this.cancelDialog.open();
                })
                .catch((error) => {
                    console.error("Failed to load the CancelBookingDialog fragment", error);
                });
        } else {
            this.cancelDialog.setModel(dialogModel, "BookingsModel");
            const context = dialogModel.createBindingContext("/");
            this.cancelDialog.setBindingContext(context, "BookingsModel");
            this.cancelDialog.getModel("BookingsModel")?.refresh();
            this.cancelDialog.open();
        }
    }

    private _bookingsPopover: any;
}
